import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useAuth } from '../../contexts';
import { useUseCases } from '../../contexts/DIContext';
import { normalizeBarcodeScanInput } from '@shared/utils';
import {
  addStockCountLine,
  approveStockCount,
  cancelStockCount,
  createStockCount,
  getStockCount,
  getStockCountActivity,
  getStockCountLines,
  getStockCountReport,
  listBranchStockCounts,
  reopenStockCount,
  startStockCount,
  StockCountApiError,
  submitStockCount,
  updateStockCountLine,
  type CountMode,
  type CountType,
  type StockCountSession,
} from '../../../data/api/stockCountApiClient';
import {
  findActiveSession,
  isActiveStatus,
  type StockCountView,
  type WizardStep,
} from './stockCountTypes';
import {
  initialStockCountState,
  initialWizardDraft,
  type StockCountViewModelState,
  type WizardDraft,
  type WizardProduct,
} from './StockCountViewModelState';

const PRODUCT_SEARCH_DEBOUNCE_MS = 300;

function notify(
  setState: Dispatch<SetStateAction<StockCountViewModelState>>,
  message: string,
  type: 'success' | 'error' | 'info' = 'info',
) {
  setState((prev) => ({
    ...prev,
    notification: { message, type, show: true },
  }));
  window.setTimeout(() => {
    setState((prev) => ({
      ...prev,
      notification: { ...prev.notification, show: false },
    }));
  }, 4000);
}

function openViewForSession(session: StockCountSession): StockCountView {
  if (session.status === 'closed' || session.status === 'cancelled') return 'report';
  if (session.status === 'review') return 'review';
  if (session.status === 'draft') return 'wizard';
  return 'count';
}

export function useStockCountViewModel() {
  const { userSession } = useAuth();
  const { getProductCategoriesUseCase, searchProductsUseCase } = useUseCases();
  const token = userSession?.token.accessToken ?? '';
  const branchId = userSession?.branchId ?? '';
  const [state, setState] = useState<StockCountViewModelState>(initialStockCountState);
  const scanFocusRef = useRef<HTMLInputElement | null>(null);
  const productScanRef = useRef<HTMLInputElement | null>(null);
  const productSearchTimerRef = useRef<number | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const loadSessions = useCallback(async () => {
    if (!token || !branchId) return;
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const result = await listBranchStockCounts(token, branchId, { page: 1, limit: 100 });
      setState((prev) => ({
        ...prev,
        sessions: result.sessions,
        sessionsPage: result.meta.page,
        hasMoreSessions: result.meta.hasMore,
        activeSession: result.active,
        isLoading: false,
      }));
    } catch (e) {
      setState((prev) => ({ ...prev, isLoading: false }));
      notify(setState, e instanceof Error ? e.message : 'Erreur chargement', 'error');
    }
  }, [token, branchId]);

  const loadMoreSessions = useCallback(async () => {
    if (!token || !branchId) return;
    const current = stateRef.current;
    if (current.isLoading || !current.hasMoreSessions) return;
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const result = await listBranchStockCounts(token, branchId, {
        page: current.sessionsPage + 1,
        limit: 100,
      });
      setState((prev) => ({
        ...prev,
        sessions: [...prev.sessions, ...result.sessions],
        sessionsPage: result.meta.page,
        hasMoreSessions: result.meta.hasMore,
        activeSession: result.active ?? prev.activeSession,
        isLoading: false,
      }));
    } catch (e) {
      setState((prev) => ({ ...prev, isLoading: false }));
      notify(setState, e instanceof Error ? e.message : 'Erreur chargement', 'error');
    }
  }, [token, branchId]);

  const loadCategories = useCallback(async () => {
    if (!branchId) return;
    try {
      const categories = await getProductCategoriesUseCase.execute(branchId);
      setState((prev) => ({
        ...prev,
        categories,
        wizard: {
          ...prev.wizard,
          categoryIds:
            prev.wizard.categoryIds.length > 0
              ? prev.wizard.categoryIds
              : categories.map((c) => c.id),
        },
      }));
    } catch {
      /* categories optional for full counts */
    }
  }, [branchId, getProductCategoriesUseCase]);

  useEffect(() => {
    void loadSessions();
    void loadCategories();
  }, [loadSessions, loadCategories]);

  useEffect(
    () => () => {
      if (productSearchTimerRef.current !== null) {
        window.clearTimeout(productSearchTimerRef.current);
      }
    },
    [],
  );

  const loadLines = useCallback(
    async (visibleId: string) => {
      setState((prev) => ({ ...prev, linesLoading: true }));
      try {
        const { lines } = await getStockCountLines(token, visibleId, {
          page: 1,
          limit: 500,
        });
        setState((prev) => {
          const nextActive =
            prev.activeLineId && lines.some((l) => l.id === prev.activeLineId)
              ? prev.activeLineId
              : lines.find((l) => l.status === 'recount' || l.status === 'pending')?.id ??
                null;
          return {
            ...prev,
            lines,
            linesLoading: false,
            activeLineId: nextActive,
          };
        });
      } catch (e) {
        setState((prev) => ({ ...prev, linesLoading: false }));
        notify(setState, e instanceof Error ? e.message : 'Erreur lignes', 'error');
      }
    },
    [token],
  );

  const refreshSelected = useCallback(
    async (visibleId: string) => {
      try {
        const session = await getStockCount(token, visibleId);
        setState((prev) => ({
          ...prev,
          selected: session,
          sessions: prev.sessions.map((s) =>
            s.visibleId === session.visibleId ? session : s,
          ),
        }));
        return session;
      } catch (e) {
        notify(setState, e instanceof Error ? e.message : 'Erreur session', 'error');
        return null;
      }
    },
    [token],
  );

  const openSession = useCallback(
    async (session: StockCountSession) => {
      const view = openViewForSession(session);
      setState((prev) => ({
        ...prev,
        selected: session,
        view,
        report: null,
        showApproveModal: false,
        draftSession: session.status === 'draft' ? session : null,
        wizardStep: session.status === 'draft' ? 'freeze' : prev.wizardStep,
      }));
      if (view === 'count' || view === 'review') {
        await loadLines(session.visibleId);
      }
      if (view === 'report') {
        try {
          const report = await getStockCountReport(token, session.visibleId);
          setState((prev) => ({ ...prev, report, selected: report.session }));
        } catch (e) {
          notify(setState, e instanceof Error ? e.message : 'Erreur rapport', 'error');
        }
      }
    },
    [loadLines, token],
  );

  const goToList = useCallback(() => {
    setState((prev) => ({
      ...prev,
      view: 'list',
      selected: null,
      lines: [],
      draftSession: null,
      report: null,
      wizardStep: 1,
      wizard: initialWizardDraft(),
      showApproveModal: false,
      showForceSubmit: false,
      confirmCancelId: null,
    }));
    void loadSessions();
  }, [loadSessions]);

  const startWizard = useCallback(() => {
    setState((prev) => ({
      ...prev,
      view: 'wizard',
      wizardStep: 1,
      wizard: {
        ...initialWizardDraft(),
        categoryIds: prev.categories.map((c) => c.id),
      },
      wizardError: '',
      productSearch: '',
      productResults: [],
      productSearching: false,
      draftSession: null,
      selected: null,
    }));
  }, []);

  const setWizardStep = useCallback((step: WizardStep) => {
    setState((prev) => ({ ...prev, wizardStep: step, wizardError: '' }));
  }, []);

  const patchWizard = useCallback((patch: Partial<WizardDraft>) => {
    setState((prev) => ({
      ...prev,
      wizard: { ...prev.wizard, ...patch },
      wizardError: '',
    }));
  }, []);

  const runProductSearch = useCallback(
    async (query: string): Promise<WizardProduct[]> => {
      if (!branchId) return [];
      const found = await searchProductsUseCase.execute(query, branchId);
      return found.map((p) => ({
        id: p.id,
        name: p.name,
        sku: p.sku,
        barcode: p.barcode,
      }));
    },
    [branchId, searchProductsUseCase],
  );

  const setProductSearch = useCallback(
    (value: string) => {
      setState((prev) => ({ ...prev, productSearch: value, wizardError: '' }));

      if (productSearchTimerRef.current !== null) {
        window.clearTimeout(productSearchTimerRef.current);
      }

      const trimmed = value.trim();
      if (trimmed.length < 2) {
        setState((prev) => ({ ...prev, productResults: [], productSearching: false }));
        return;
      }

      setState((prev) => ({ ...prev, productSearching: true }));
      productSearchTimerRef.current = window.setTimeout(async () => {
        try {
          const results = await runProductSearch(trimmed);
          setState((prev) =>
            prev.productSearch.trim() === trimmed
              ? { ...prev, productResults: results, productSearching: false }
              : prev,
          );
        } catch (e) {
          setState((prev) => ({ ...prev, productSearching: false }));
          notify(setState, e instanceof Error ? e.message : 'Erreur recherche', 'error');
        }
      }, PRODUCT_SEARCH_DEBOUNCE_MS);
    },
    [runProductSearch],
  );

  const addWizardProduct = useCallback((product: WizardProduct) => {
    if (productSearchTimerRef.current !== null) {
      window.clearTimeout(productSearchTimerRef.current);
    }
    setState((prev) => ({
      ...prev,
      wizard: {
        ...prev.wizard,
        products: prev.wizard.products.some((p) => p.id === product.id)
          ? prev.wizard.products
          : [...prev.wizard.products, product],
      },
      productSearch: '',
      productResults: [],
      productSearching: false,
      wizardError: '',
    }));
    window.setTimeout(() => productScanRef.current?.focus(), 50);
  }, []);

  const removeWizardProduct = useCallback((productId: string) => {
    setState((prev) => ({
      ...prev,
      wizard: {
        ...prev.wizard,
        products: prev.wizard.products.filter((p) => p.id !== productId),
      },
      wizardError: '',
    }));
  }, []);

  /** Enter on the perimeter scan field: exact barcode/SKU match is added straight away. */
  const submitProductScan = useCallback(async () => {
    const query = normalizeBarcodeScanInput(stateRef.current.productSearch).trim();
    if (!query) return;

    const matchesExactly = (p: WizardProduct) =>
      p.barcode === query || p.sku.toLowerCase() === query.toLowerCase();

    const known = stateRef.current.productResults.find(matchesExactly);
    if (known) {
      addWizardProduct(known);
      return;
    }

    setState((prev) => ({ ...prev, productSearching: true }));
    try {
      const results = await runProductSearch(query);
      const exact = results.find(matchesExactly);
      if (exact) {
        addWizardProduct(exact);
        return;
      }
      setState((prev) => ({
        ...prev,
        productResults: results,
        productSearching: false,
        wizardError: results.length === 0 ? `Produit introuvable — ${query}` : '',
      }));
    } catch (e) {
      setState((prev) => ({ ...prev, productSearching: false }));
      notify(setState, e instanceof Error ? e.message : 'Erreur recherche', 'error');
    }
  }, [addWizardProduct, runProductSearch]);

  /** Perimeter step must be complete before leaving it. */
  const goToNextWizardStep = useCallback(() => {
    const { wizard, wizardStep } = stateRef.current;
    const stepNum = typeof wizardStep === 'number' ? wizardStep : 1;

    if (stepNum === 2) {
      if (wizard.type === 'spot' && wizard.products.length === 0) {
        setState((prev) => ({
          ...prev,
          wizardError: 'Sélectionnez au moins un produit',
        }));
        return;
      }
      if (wizard.type === 'cycle' && wizard.categoryIds.length === 0) {
        setState((prev) => ({
          ...prev,
          wizardError: 'Sélectionnez au moins une catégorie',
        }));
        return;
      }
    }

    setState((prev) => ({
      ...prev,
      wizardStep: (stepNum + 1) as WizardStep,
      wizardError: '',
    }));
  }, []);

  const createDraft = useCallback(async () => {
    if (!token || !branchId) return;
    const wizard = stateRef.current.wizard;

    if (wizard.type === 'spot' && wizard.products.length === 0) {
      setState((prev) => ({
        ...prev,
        wizardStep: 2,
        wizardError: 'Sélectionnez au moins un produit',
      }));
      return;
    }
    if (wizard.type === 'cycle' && wizard.categoryIds.length === 0) {
      setState((prev) => ({
        ...prev,
        wizardStep: 2,
        wizardError: 'Sélectionnez au moins une catégorie',
      }));
      return;
    }

    setState((prev) => ({ ...prev, isSaving: true, wizardError: '' }));
    try {
      const payload = {
        branchId,
        type: wizard.type,
        countMode: wizard.countMode,
        categoryIds:
          wizard.type === 'cycle' && wizard.categoryIds.length
            ? wizard.categoryIds
            : undefined,
        // FO ProductRepository maps domain id ← visibleId; start() queries Product.visibleId.
        productIds:
          wizard.type === 'spot' && wizard.products.length
            ? wizard.products.map((p) => p.id)
            : undefined,
        notes: wizard.notes.trim() || undefined,
        thresholdUnits: wizard.thresholdUnits,
        thresholdPercent: wizard.thresholdPercent,
      };
      const session = await createStockCount(token, payload);
      setState((prev) => ({
        ...prev,
        isSaving: false,
        draftSession: session,
        selected: session,
        wizardStep: 'freeze',
        sessions: [session, ...prev.sessions.filter((s) => s.visibleId !== session.visibleId)],
      }));
      notify(setState, 'Brouillon créé', 'success');
    } catch (e) {
      setState((prev) => ({ ...prev, isSaving: false }));
      if (e instanceof StockCountApiError && e.status === 409) {
        notify(setState, 'Un inventaire est déjà en cours', 'error');
        const listed = await listBranchStockCounts(token, branchId, { page: 1, limit: 100 });
        const active = listed.active ?? findActiveSession(listed.sessions);
        setState((prev) => ({
          ...prev,
          sessions: listed.sessions,
          sessionsPage: listed.meta.page,
          hasMoreSessions: listed.meta.hasMore,
          activeSession: listed.active,
        }));
        if (active) await openSession(active);
        return;
      }
      const message = e instanceof Error ? e.message : 'Erreur création';
      // 400s describe an invalid perimeter, so send the user back to fix it.
      if (e instanceof StockCountApiError && e.status === 400) {
        setState((prev) => ({ ...prev, wizardStep: 2, wizardError: message }));
      }
      notify(setState, message, 'error');
    }
  }, [token, branchId, openSession]);

  const setScanValue = useCallback((value: string) => {
    setState((prev) => ({ ...prev, scanValue: normalizeBarcodeScanInput(value) }));
  }, []);

  const selectLineByScan = useCallback(async () => {
    const prev = stateRef.current;
    const q = normalizeBarcodeScanInput(prev.scanValue).trim();
    const sessionId = prev.selected?.visibleId ?? '';
    if (!q || !sessionId || !token) {
      window.setTimeout(() => scanFocusRef.current?.focus(), 50);
      return;
    }

    const found = prev.lines.find(
      (l) =>
        l.barcode === q ||
        l.sku.toLowerCase() === q.toLowerCase() ||
        l.productId === q,
    );
    if (found) {
      setState((current) => ({
        ...current,
        activeLineId: found.id,
        scanValue: '',
        keyInput: '',
        keyMode: 'stock',
        lineError: '',
      }));
      window.setTimeout(() => scanFocusRef.current?.focus(), 50);
      return;
    }

    try {
      const added = await addStockCountLine(token, sessionId, { q, barcode: q });
      setState((current) => {
        const exists = current.lines.some((l) => l.id === added.line.id);
        return {
          ...current,
          lines: exists
            ? current.lines.map((l) => (l.id === added.line.id ? added.line : l))
            : [...current.lines, added.line],
          selected: current.selected
            ? { ...current.selected, progress: added.progress }
            : current.selected,
          activeLineId: added.line.id,
          scanValue: '',
          keyInput: '',
          keyMode: 'stock',
          lineError: '',
        };
      });
      notify(
        setState,
        added.created
          ? `Produit ajouté à l’inventaire — ${added.line.productName}`
          : `Produit sélectionné — ${added.line.productName}`,
        'success',
      );
    } catch {
      notify(setState, 'Produit introuvable dans cet inventaire', 'error');
      setState((current) => ({ ...current, scanValue: '' }));
    }
    window.setTimeout(() => scanFocusRef.current?.focus(), 50);
  }, [token]);

  const setActiveLineId = useCallback((id: string | null) => {
    setState((prev) => ({
      ...prev,
      activeLineId: id,
      keyInput: '',
      keyMode: 'stock',
      lineError: '',
    }));
  }, []);

  const setKeyMode = useCallback((mode: 'stock' | 'exposed') => {
    setState((prev) => ({ ...prev, keyMode: mode, keyInput: '', lineError: '' }));
  }, []);

  const keyPress = useCallback(
    async (key: string) => {
      if (key === '⌫') {
        setState((prev) => ({ ...prev, keyInput: prev.keyInput.slice(0, -1), lineError: '' }));
        return;
      }
      if (key !== 'OK') {
        setState((prev) => ({
          ...prev,
          keyInput: (prev.keyInput + key).slice(0, 6),
          lineError: '',
        }));
        return;
      }

      const snapshot = stateRef.current;
      const line = snapshot.lines.find((l) => l.id === snapshot.activeLineId);
      const session = snapshot.selected;
      if (!line || !session || !token || snapshot.keyInput === '') return;

      const val = parseInt(snapshot.keyInput, 10);
      if (Number.isNaN(val) || val < 0) {
        setState((prev) => ({ ...prev, lineError: 'Quantité invalide' }));
        return;
      }

      const mode = session.countMode;
      let countedStock = line.countedStock;
      let countedExposed = line.countedExposed;

      if (mode === 'floor_only') {
        countedExposed = val;
        countedStock = line.countedStock ?? line.theoreticalStock;
      } else if (snapshot.keyMode === 'stock') {
        countedStock = val;
      } else {
        countedExposed = val;
      }

      if (mode === 'dual') {
        const stockVal = countedStock ?? 0;
        const expVal = countedExposed;
        if (snapshot.keyMode === 'exposed' && expVal != null && expVal > stockVal) {
          setState((prev) => ({
            ...prev,
            lineError: "L'exposé ne peut pas dépasser le stock compté",
          }));
          return;
        }
      }

      setState((prev) => ({ ...prev, isSaving: true, lineError: '' }));
      try {
        const payload =
          mode === 'floor_only'
            ? { countedExposed: val, source: 'pos' as const }
            : snapshot.keyMode === 'stock'
              ? {
                  countedStock: val,
                  ...(mode === 'dual' && countedExposed != null
                    ? { countedExposed }
                    : {}),
                  source: 'pos' as const,
                }
              : {
                  countedStock: countedStock ?? line.theoreticalStock,
                  countedExposed: val,
                  source: 'pos' as const,
                };

        const { line: updated, progress } = await updateStockCountLine(
          token,
          session.visibleId,
          line.id,
          payload,
        );

        setState((prev) => {
          const lines = prev.lines.map((l) => (l.id === updated.id ? updated : l));
          const selected = prev.selected
            ? { ...prev.selected, progress }
            : prev.selected;

          if (mode === 'dual' && snapshot.keyMode === 'stock') {
            return {
              ...prev,
              lines,
              selected,
              isSaving: false,
              keyMode: 'exposed',
              keyInput: '',
            };
          }

          const next = lines.find(
            (l) =>
              l.id !== updated.id &&
              (l.status === 'pending' || l.status === 'recount'),
          );
          return {
            ...prev,
            lines,
            selected,
            isSaving: false,
            activeLineId: next?.id ?? null,
            keyMode: 'stock',
            keyInput: '',
          };
        });
        window.setTimeout(() => scanFocusRef.current?.focus(), 50);
      } catch (e) {
        setState((prev) => ({ ...prev, isSaving: false }));
        notify(setState, e instanceof Error ? e.message : 'Erreur saisie', 'error');
      }
    },
    [token],
  );

  const submitForReview = useCallback(async (force = false) => {
    const snapshot = stateRef.current;
    const session = snapshot.selected;
    if (!session || !token) return;

    const pending = snapshot.lines.filter(
      (l) => l.status === 'pending' || l.status === 'recount',
    ).length;

    if (session.type === 'full' && pending > 0 && !force) {
      setState((prev) => ({ ...prev, showForceSubmit: true }));
      return;
    }

    if (force && !snapshot.forceReason.trim()) {
      notify(setState, 'Motif obligatoire pour forcer la soumission', 'error');
      return;
    }

    setState((prev) => ({ ...prev, isSaving: true }));
    try {
      const updated = await submitStockCount(token, session.visibleId, {
        force: force || undefined,
        reason: force ? snapshot.forceReason.trim() : undefined,
      });
      setState((prev) => ({
        ...prev,
        isSaving: false,
        selected: updated,
        view: 'review',
        showForceSubmit: false,
        forceReason: '',
        sessions: prev.sessions.map((s) =>
          s.visibleId === updated.visibleId ? updated : s,
        ),
      }));
      await loadLines(updated.visibleId);
      notify(setState, 'Soumis pour revue', 'success');
    } catch (e) {
      setState((prev) => ({ ...prev, isSaving: false }));
      notify(setState, e instanceof Error ? e.message : 'Erreur soumission', 'error');
    }
  }, [token, loadLines]);

  const reopenCounting = useCallback(async () => {
    const session = stateRef.current.selected;
    if (!session || !token) return;
    setState((prev) => ({ ...prev, isSaving: true }));
    try {
      const updated = await reopenStockCount(token, session.visibleId);
      setState((prev) => ({
        ...prev,
        isSaving: false,
        selected: updated,
        view: 'count',
        sessions: prev.sessions.map((s) =>
          s.visibleId === updated.visibleId ? updated : s,
        ),
      }));
      await loadLines(updated.visibleId);
    } catch (e) {
      setState((prev) => ({ ...prev, isSaving: false }));
      notify(setState, e instanceof Error ? e.message : 'Erreur', 'error');
    }
  }, [token, loadLines]);

  const markRecount = useCallback(
    async (lineId: string) => {
      const session = stateRef.current.selected;
      if (!session || !token) return;
      try {
        let current = session;
        // Recount from review requires the session to be counting again for quantity edits.
        if (current.status === 'review') {
          current = await reopenStockCount(token, current.visibleId);
          setState((prev) => ({
            ...prev,
            selected: current,
            sessions: prev.sessions.map((s) =>
              s.visibleId === current.visibleId ? current : s,
            ),
          }));
        }

        const { line, progress } = await updateStockCountLine(
          token,
          current.visibleId,
          lineId,
          { markRecount: true, source: 'pos' },
        );
        setState((prev) => ({
          ...prev,
          lines: prev.lines.map((l) => (l.id === line.id ? line : l)),
          selected: prev.selected ? { ...prev.selected, progress } : prev.selected,
          view: 'count',
          activeLineId: line.id,
          keyInput: '',
          keyMode: 'stock',
        }));
      } catch (e) {
        notify(setState, e instanceof Error ? e.message : 'Erreur', 'error');
      }
    },
    [token],
  );

  const loadActivity = useCallback(async () => {
    const session = stateRef.current.selected;
    if (!session || !token) return;
    try {
      const activity = await getStockCountActivity(token, session.visibleId);
      setState((prev) => ({ ...prev, activity, showActivity: true }));
    } catch (e) {
      notify(setState, e instanceof Error ? e.message : 'Erreur activité', 'error');
    }
  }, [token]);

  const confirmApprove = useCallback(async () => {
    const snapshot = stateRef.current;
    const session = snapshot.selected;
    if (!session || !token) return;
    const high = session.variance.highVarianceLines > 0;
    if (high && !snapshot.acknowledgeHighVariance) {
      notify(setState, 'Reconnaissez les écarts élevés avant validation', 'error');
      return;
    }
    setState((prev) => ({ ...prev, isSaving: true }));
    try {
      const result = await approveStockCount(token, session.visibleId, {
        comment: snapshot.approveComment.trim() || undefined,
        acknowledgeHighVariance: high ? true : undefined,
      });
      setState((prev) => ({
        ...prev,
        isSaving: false,
        showApproveModal: false,
        selected: result.session,
        view: 'report',
        sessions: prev.sessions.map((s) =>
          s.visibleId === result.session.visibleId ? result.session : s,
        ),
      }));
      try {
        const report = await getStockCountReport(token, result.session.visibleId);
        setState((prev) => ({ ...prev, report }));
      } catch {
        /* report optional after approve */
      }
      notify(setState, 'Stock virtuel aligné sur le physique', 'success');
    } catch (e) {
      setState((prev) => ({ ...prev, isSaving: false }));
      notify(setState, e instanceof Error ? e.message : 'Erreur validation', 'error');
    }
  }, [token]);

  const startCounting = useCallback(async () => {
    const draft = stateRef.current.draftSession ?? stateRef.current.selected;
    if (!draft || !token) return;
    setState((prev) => ({ ...prev, isSaving: true }));
    try {
      const session = await startStockCount(token, draft.visibleId);
      setState((prev) => ({
        ...prev,
        isSaving: false,
        selected: session,
        draftSession: null,
        view: 'count',
        sessions: [
          session,
          ...prev.sessions.filter((s) => s.visibleId !== session.visibleId),
        ],
      }));
      await loadLines(session.visibleId);
      notify(setState, 'Comptage démarré — stock théorique figé', 'success');
    } catch (e) {
      setState((prev) => ({ ...prev, isSaving: false }));
      if (e instanceof StockCountApiError && e.status === 409) {
        notify(setState, 'Un inventaire est déjà en cours', 'error');
        const listed = await listBranchStockCounts(token, branchId, { page: 1, limit: 100 });
        const active = listed.active ?? findActiveSession(listed.sessions);
        setState((prev) => ({
          ...prev,
          sessions: listed.sessions,
          sessionsPage: listed.meta.page,
          hasMoreSessions: listed.meta.hasMore,
          activeSession: listed.active,
        }));
        if (active) await openSession(active);
        return;
      }
      notify(setState, e instanceof Error ? e.message : 'Erreur démarrage', 'error');
    }
  }, [token, loadLines, branchId, openSession]);

  const cancelSession = useCallback(
    async (visibleId: string) => {
      if (!token) return;
      setState((prev) => ({ ...prev, isSaving: true, confirmCancelId: null }));
      try {
        const updated = await cancelStockCount(token, visibleId);
        setState((prev) => ({
          ...prev,
          isSaving: false,
          sessions: prev.sessions.map((s) =>
            s.visibleId === updated.visibleId ? updated : s,
          ),
          view: 'list',
          selected: null,
          lines: [],
        }));
        notify(setState, 'Inventaire annulé', 'info');
      } catch (e) {
        setState((prev) => ({ ...prev, isSaving: false }));
        notify(setState, e instanceof Error ? e.message : 'Erreur annulation', 'error');
      }
    },
    [token],
  );

  const intents = {
    loadSessions,
    loadMoreSessions,
    goToList,
    startWizard,
    setWizardStep,
    goToNextWizardStep,
    patchWizard,
    setProductSearch,
    submitProductScan,
    addWizardProduct,
    removeWizardProduct,
    createDraft,
    startCounting,
    openSession,
    setScanValue,
    selectLineByScan,
    setActiveLineId,
    setKeyMode,
    keyPress,
    submitForReview,
    reopenCounting,
    markRecount,
    loadActivity,
    confirmApprove,
    cancelSession,
    setShowApproveModal: (show: boolean) =>
      setState((prev) => ({
        ...prev,
        showApproveModal: show,
        acknowledgeHighVariance: false,
        approveComment: '',
      })),
    setApproveComment: (v: string) =>
      setState((prev) => ({ ...prev, approveComment: v })),
    setAcknowledgeHighVariance: (v: boolean) =>
      setState((prev) => ({ ...prev, acknowledgeHighVariance: v })),
    setShowForceSubmit: (show: boolean) =>
      setState((prev) => ({ ...prev, showForceSubmit: show, forceReason: '' })),
    setForceReason: (v: string) => setState((prev) => ({ ...prev, forceReason: v })),
    setShowActivity: (show: boolean) =>
      setState((prev) => ({ ...prev, showActivity: show })),
    setConfirmCancelId: (id: string | null) =>
      setState((prev) => ({ ...prev, confirmCancelId: id })),
    setView: (view: StockCountView) => setState((prev) => ({ ...prev, view })),
    dismissNotification: () =>
      setState((prev) => ({
        ...prev,
        notification: { ...prev.notification, show: false },
      })),
    registerScanRef: (el: HTMLInputElement | null) => {
      scanFocusRef.current = el;
    },
    registerProductScanRef: (el: HTMLInputElement | null) => {
      productScanRef.current = el;
    },
    setWizardType: (type: CountType) => patchWizard({ type }),
    setWizardMode: (countMode: CountMode) => patchWizard({ countMode }),
    refreshSelected,
    isActiveStatus,
  };

  return { state, intents, scanFocusRef, productScanRef };
}
